import { useState, useEffect, useContext, createContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import ApiService from '../services/api';

// Create Menu Context
const MenuContext = createContext();

// Menu Provider Component
export const MenuProvider = ({ children }) => {
  const [userMenus, setUserMenus] = useState([]);
  const [menuPermissions, setMenuPermissions] = useState({});
  const [routeMenuKeyMap, setRouteMenuKeyMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const location = useLocation();

  // Check if current page is login or register
  const isAuthPage = () => {
    const currentPath = window.location.pathname;
    return currentPath.includes('/login') || currentPath.includes('/register') || currentPath.includes('/auth');
  };

  // Debounce mechanism to prevent rapid successive fetches
  const shouldFetch = (force = false) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    return force || timeSinceLastFetch > 2000; // At least 2 seconds between fetches
  };

  // Fetch user menus and permissions
  const fetchUserMenus = async (force = false) => {
    // Skip if not forced and too soon since last fetch
    if (!shouldFetch(force)) {
      console.log('⏸️ Skipping menu fetch - too soon since last fetch');
      return;
    }

    try {      
      setLoading(true);
      setError(null);
      setLastFetchTime(Date.now());

      // Skip fetching if on auth pages or no token
      const token = localStorage.getItem('accessToken');
      if (!token || isAuthPage()) {
        console.log('⏸️ Skipping menu fetch - no token or on auth page');
        setUserMenus([]);
        setMenuPermissions({});
        setLoading(false);
        return;
      }

      const response = await ApiService.getUserMenus();
      console.log('✅ Menu fetch response:', response);
      
      if (response.status === 200) {
        const menus = response.data.menus || [];
        setUserMenus(menus);
        
        // Build permissions map for quick lookup
        const permissionsMap = {};
        const routeMap = {};
        
        const buildMaps = (menuItems) => {
          menuItems.forEach(menu => {
            // Add permissions
            if (menu.permissions) {
              permissionsMap[menu.key] = menu.permissions;
            }
            
            // Build route to menu key mapping from URL
            if (menu.url && menu.url !== '') {
              routeMap[menu.url] = menu.key;
              // console.log(`🗺️ Route mapped: ${menu.url} → ${menu.key}`);
            }
            
            // Recursively process children
            if (menu.children && menu.children.length > 0) {
              buildMaps(menu.children);
            }
          });
        };
        
        buildMaps(menus);
        setMenuPermissions(permissionsMap);
        setRouteMenuKeyMap(routeMap);
        console.log('✅ Menus loaded successfully:', { 
          menuCount: menus.length, 
          permissionsCount: Object.keys(permissionsMap).length,
          routesCount: Object.keys(routeMap).length 
        });
      }
    } catch (err) {
      console.error('❌ Error fetching user menus:', err);
      setError(err.message || 'Failed to fetch user menus');
      setUserMenus([]);
      setMenuPermissions({});
    } finally {
      setLoading(false);
    }
  };

  // Check if user has specific permission for a menu
  const hasPermission = (menuKey, action = 'read') => {
    const permissions = menuPermissions[menuKey];
    
    if (!permissions) {
      console.warn(`⚠️ No permissions found for menu key: ${menuKey}`);
      return false;
    }

    let hasAccess = false;
    switch (action) {
      case 'create':
        hasAccess = permissions.can_create === true;
        break;
      case 'read':
        hasAccess = permissions.can_read === true;
        break;
      case 'update':
        hasAccess = permissions.can_update === true;
        break;
      case 'delete':
        hasAccess = permissions.can_delete === true;
        break;
      default:
        console.warn(`⚠️ Unknown action: ${action}`);
        hasAccess = false;
    }

    return hasAccess;
  };

  // Check if user can access a menu (has read permission)
  const canAccessMenu = (menuKey) => {
    return hasPermission(menuKey, 'read');
  };

  // Memoize the accessible menus to prevent unnecessary re-renders
  const memoizedAccessibleMenus = useMemo(() => {
    const filterMenus = (menus) => {
      return menus.filter(menu => {
        // For title menus, check if user has read permission
        if (menu.isTitle) {
          return canAccessMenu(menu.key);
        }
        
        // For parent menus with children
        if (menu.children && menu.children.length > 0) {
          // First filter the children
          menu.children = filterMenus(menu.children);
          
          // Show parent menu if:
          // 1. Parent has read permission, OR
          // 2. At least one child is accessible
          const parentHasAccess = canAccessMenu(menu.key);
          const hasAccessibleChildren = menu.children.length > 0;
          
          return parentHasAccess || hasAccessibleChildren;
        }
        
        // For leaf menus, check if user has read permission
        return canAccessMenu(menu.key);
      });
    };

    return filterMenus([...userMenus]);
  }, [userMenus, menuPermissions]);

  // Filter menus based on permissions (kept for backward compatibility)
  const getAccessibleMenus = () => {
    return memoizedAccessibleMenus;
  };

  // Refresh menus (useful after role changes or login)
  const refreshMenus = (force = false) => {
    const token = localStorage.getItem('accessToken');
    if (token && !isAuthPage() && (force || userMenus.length === 0)) {
      console.log('🔄 Refreshing menus...', { force, hasMenus: userMenus.length > 0 });
      fetchUserMenus(force);
    }
  };

  // Initial load and route changes - only fetch when transitioning from auth to non-auth pages
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const isAuth = isAuthPage();

    if (token && !isAuth && userMenus.length === 0) {
      // Only fetch if we don't have menus yet - force initial load
      fetchUserMenus(true);
    } else if (isAuth) {
      // Clear menus when on auth pages
      setUserMenus([]);
      setMenuPermissions({});
      setLoading(false);
    }
  }, [location.pathname]);

  // Listen for custom login event
  useEffect(() => {
    const handleLogin = () => {
      console.log('🔑 Login event received');
      // Small delay to ensure token is set, force refresh menus
      setTimeout(() => {
        refreshMenus(true);
      }, 100);
    };

    // Listen for custom login event
    window.addEventListener('userLoggedIn', handleLogin);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
    };
  }, []);

  // Listen for storage changes (token updates from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken') {
        const token = e.newValue;
        
        if (token && !isAuthPage() && userMenus.length === 0) {
          fetchUserMenus(true);
        } else if (!token) {
          setUserMenus([]);
          setMenuPermissions({});
          setLoading(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userMenus.length]);

  const value = {
    userMenus,
    menuPermissions,
    routeMenuKeyMap,
    loading,
    error,
    hasPermission,
    canAccessMenu,
    getAccessibleMenus,
    refreshMenus
  };

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  );
};

// Hook to use menu context
export const useMenuPermissions = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenuPermissions must be used within a MenuProvider');
  }
  return context;
};

// Custom hook for checking permissions
export const usePermission = (menuKey, action = 'read') => {
  const { hasPermission } = useMenuPermissions();
  return hasPermission(menuKey, action);
};

// Custom hook for menu access
export const useMenuAccess = (menuKey) => {
  const { canAccessMenu } = useMenuPermissions();
  return canAccessMenu(menuKey);
};
